import { Skeleton } from "@mui/material";
import dayjs from "dayjs";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API_URL } from "../../../../api";
import chatPlaceholder from "../../../../assets/images/chatPlaceholder.svg";
import { useSocket } from "../../../../Context/SockerContext";
import { useCookies } from "../../../../hooks/useCookies";
import styles from "./matches.module.css";
import { MdClose } from "react-icons/md";
import UpgradeModal from "../upgradenow/upgradenow";
import { getProfileImageUrl } from "../../../../utils/serverUtils";
import { useAppContext } from "../../../../Context/UseContext";
import { getBlockedList } from "../../../../actions/match";
import * as messageActon from "../../../../actions/Message";
import Loader from "../../../Utility/Loader";

export default function Matches() {
  const [matches, setMatches] = useState([]);
  const [currentMatches, setCurrentMatches] = useState([]);
  const [loadingMatches, setLoadingMatches] = useState(true);
  const [conversations, setConversations] = useState([]);
  const [showmodal, setshowmodal] = useState(false);
  const cookies = useCookies();
  const [page, setPage] = useState(1);
  const navigate = useNavigate();
  const { socket } = useSocket();
  const dialogRef = useRef(null);
  const [imageURL, setImageURL] = useState("");
  const token = cookies.getCookie("token");
  const hasFetched = useRef(false);
  const { isPremium } = useAppContext();
  const hasFetchedRef = useRef(false);
  const [chatLoading, setChatLoading] = useState(false);
  const [blockedUsers, setBlockedUsers] = useState(new Set());

  useEffect(() => {
    // Fetch blocked list
    (async () => {
      const list = await getBlockedList();
      if (Array.isArray(list)) {
        // Assume list contains objects with user_id or id, or just IDs? 
        // Checking match.jsx logs or assumptions. Usually it returns user objects or list of blocks.
        // Let's assume it returns list of blocked users. 
        // We need to map to IDs.
        setBlockedUsers(new Set(list.map(u => u.user_id || u.id))); 
      }
    })();
  }, []);

  const fetchMatches = useCallback(async () => {
    try {
      setLoadingMatches(true);
      const response = await messageActon.getMatches(page);
      const data = await response;
      if (data) {
        setMatches((prevRequests) => {
          const uniqueRequests = new Map();
          [...prevRequests, ...data].forEach((req) =>
            uniqueRequests.set(req.user_id, req)
          );
          return Array.from(uniqueRequests.values());
        });
        setCurrentMatches(data);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingMatches(false);
    }
  }, [page]);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    fetchMatches();
  }, [fetchMatches]);

  useEffect(() => {
    if (currentMatches?.length < 10) return;

    const lastProfileObserver = new IntersectionObserver((entries) => {
      const lastProfile = entries[0];

      if (!lastProfile.isIntersecting) return;

      setPage((p) => p + 1);
    });

    const lastEl = document.querySelector(`.profile:last-child`);
    if (lastEl) lastProfileObserver.observe(lastEl);

    return () => {
      lastProfileObserver.disconnect();
    };
  }, [currentMatches]);

  const getConversations = useCallback(() => {
    (async () => {
      const response = await fetch(
        `${API_URL}customer/chat/get-conversations`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const data = await response.json();
      if (response.ok) {
        setConversations(data);
      }
    })();
  }, [token]);

  const fetchConversations = useCallback(() => {
    socket?.on("fetch-messages", async () => {
      const response = await messageActon.getLastedConversion();
      const result = await response;
      if (result) {
        window.dispatchEvent(
          new CustomEvent("newChatReceived", { detail: result })
        );
        setConversations((prevConversations) => [
          result[0],
          ...prevConversations?.filter(
            (conversation) =>
              conversation?.conversation_id !== result[0]?.conversation_id
          ),
        ]);
      }
    });

    return () => {
      socket?.off("fetch-messages");
    };
  }, [socket]);

  const blockConversions = useCallback(() => {
    socket?.on("block", (data) => {
      const personId = data.personId;
      setBlockedUsers((prev) => new Set(prev).add(personId));
    });

    socket?.on("unblock", (data) => {
      const personId = data.personId;
      setBlockedUsers((prev) => {
        const newSet = new Set(prev);
        newSet.delete(personId);
        return newSet;
      });
    });

    return () => {
      socket?.off("block");
      socket?.off("unblock");
    };
  }, [socket]);

  useEffect(() => {
    if (hasFetchedRef.current) return;

    hasFetchedRef.current = true;
    getConversations();
    fetchConversations();
    blockConversions();
  }, [getConversations, fetchConversations, blockConversions]);

  async function getRoom(userId, name, img, token) {
    if (isPremium !== true) {
      setshowmodal(true);
      return;
    }

    await chatWith({
      name: name,
      recepientId: userId,
      token: token
    });
  }

  async function chatWith(payload) {
    try {
      setChatLoading(true);
      const response = await messageActon.createChatRoom({
        participantId: payload?.recepientId,
      });
      if (response.conversationId) {
        if (sessionStorage.getItem("conversationId")) {
          socket?.emit("leave-room", sessionStorage.getItem("conversationId"));
        }

        await messageActon.messageSeen(response.conversationId);
        navigate(`/user/chat/with/${payload?.recepientId}?token=${encodeURIComponent(payload?.token || "")}`, {
          state: { conversationId: response.conversationId }
        });
      } else {
        setChatLoading(false);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setChatLoading(false);
    }
  }

  function getDateDifference(date) {
    if (!date) return "";
    const today = dayjs();
    const messageDate = dayjs(date);
    
    if (!messageDate.isValid()) return "";

    const diff = today.diff(messageDate, "day");
    const diffInHours = today.diff(messageDate, "hour");
    const diffInMinutes = today.diff(messageDate, "minute");

    if (diff === 0) {
      if (diffInHours < 1) {
        if (diffInMinutes >= 1) {
          return `${diffInMinutes} minutes ago`;
        } else {
          return "Just Now";
        }
      } else {
        return `${diffInHours} ${diffInHours === 1 ? "hour" : "hours"} ago`;
      }
    } else if (diff === 1) {
      return "Yesterday";
    } else {
      return messageDate.format("DD/MM/YYYY");
    }
  }

  return (
    <section className={styles.matchesContainerRoot}>
      {chatLoading && <Loader />}

      <div className={styles.newMatchesSection}>
        <p className="h4 mb-3">Matches</p>

        {!loadingMatches &&
          Array.isArray(matches) &&
          matches?.length === 0 &&
          Array.isArray(conversations) &&
          conversations?.length === 0 && (
            <div className={styles.chatPlaceholder}>
              <img src={chatPlaceholder} alt="chat placeholder" />
              <p>
                You don't have any chats yet. Make the first move & say hello!
              </p>
            </div>
          )}

        {loadingMatches && (
          <div className={styles.profilesContainer}>
            {Array(5)
              .fill(0)
              .map((_, i) => (
                <div key={i}>
                  <Skeleton
                    animation="pulse"
                    variant="circular"
                    width={75}
                    height={75}
                  />
                </div>
              ))}
          </div>
        )}

        <div className={styles.profilesContainer}>
          {matches?.length !== 0 ? (
            <>
              {Array.isArray(matches) &&
                matches?.map((match) => (
                  <div
                    key={match.user_id}
                    className={styles.profile}
                    onClick={() =>
                      getRoom(
                        match.user_id,
                        match?.first_name,
                        getProfileImageUrl(match),
                        match?.profile_token
                      )
                    }
                  >
                    <img
                      src={getProfileImageUrl(match)}
                      alt="profile"
                      style={{
                        width: "70px",
                        height: "70px",
                        objectFit: "cover",
                      }}
                    />
                    <span>{match?.first_name}</span>
                  </div>
                ))}
            </>
          ) : (
            <div className={styles.chatNoRecodePlaceholder}>
              {conversations.length !== 0 && (
                <p>You don't have any matches yet.</p>
              )}
            </div>
          )}
        </div>
      </div>

      {conversations?.length !== 0 && (
        <div className={styles.messagesSection}>
          <p className="h4 mb-3 ms-2">Messages</p>
          <div className={styles.messagesContainer}>
            {conversations?.map((conversation, i) => (
              <div
                key={i}
                className={styles.message}
                onClick={() =>
                  getRoom(
                    conversation?.user_id,
                    conversation?.first_name,
                    getProfileImageUrl(conversation),
                    conversation?.profile_token
                  )
                }
              >
                <img
                  src={getProfileImageUrl(conversation)}
                  alt="profile"
                  style={{
                    width: "70px",
                    height: "70px",
                    objectFit: "cover",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    dialogRef.current.showModal();
                    setImageURL(getProfileImageUrl(conversation));
                  }}
                />
                <div>
                  <p
                    style={{
                      fontSize: "clamp(14px, 4vw, 16px)",
                      fontWeight: "600",
                      marginBottom: "4px",
                    }}
                  >
                    {conversation?.first_name}{" "}
                  </p>
                  <p
                    style={{
                      filter: isPremium === true ? "none" : "blur(5px)",
                      fontSize: "clamp(12px, 3.5vw, 14px)",
                      lineHeight: "1.4",
                      margin: "0",
                    }}
                  >
                    {conversation?.message}
                  </p>
                  {blockedUsers.has(conversation?.user_id) && (
                    <span className={styles.blockedBadge}>
                      BLOCKED
                    </span>
                  )}
                </div>
                <div style={{ flexGrow: "1" }}></div>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                    gap: "4px",
                  }}
                >
                  <p className={styles.dateText}>
                    {getDateDifference(conversation.sent_at)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <dialog ref={dialogRef} className={styles.profileDialog}>
        <MdClose
          size={30}
          color="white"
          onClick={() => dialogRef.current.close()}
          style={{
            position: "absolute",
            right: "1rem",
            top: "1rem",
            cursor: "pointer",
          }}
        />
        <img src={imageURL} alt="profile" />
      </dialog>
      <UpgradeModal show={showmodal} setShow={setshowmodal} />
    </section>
  );
}
